import get from 'lodash/get';
import {
  Field,
  FieldItemProps,
  Group,
  RecordItem,
  SimpleFieldItemProps,
} from './RecordPreviewProps';
import { NuvoLink } from '@nuvolo/nuux-core/components/NuvoLink';
import Anchor from '../Anchor';
import { parseLocalDateString } from '#/common/utils/datetimeTransform';

const getFieldValue = <T,>(field: Field<T>, record: T, emptyValue: string) => {
  return get(record, field.dataField) ?? emptyValue;
};

const handleDateType = <T,>(
  result: SimpleFieldItemProps,
  field: Field<T>,
  record: T,
  emptyValue: string,
): SimpleFieldItemProps => ({
  ...result,
  value: record[field.dataField as keyof T]
    ? parseLocalDateString(String(result.value)).toLocaleDateString()
    : emptyValue,
});

const handleDateTimeType = <T,>(
  result: SimpleFieldItemProps,
  field: Field<T>,
  record: T,
  emptyValue: string,
): SimpleFieldItemProps => ({
  ...result,
  value: record[field.dataField as keyof T] ? new Date(result.value).toLocaleString() : emptyValue,
});

const handleLinkType = <T,>(
  result: SimpleFieldItemProps,
  field: Field<T>,
  record: T,
  navigate?: (url: string) => void,
): SimpleFieldItemProps => {
  if (!record[field.valueExpr as keyof T]) return result;

  const path = field.url?.replace(':id', record[field.valueExpr as keyof T] as string);

  return {
    ...result,
    render: () => (
      <Anchor path={path}>
        <NuvoLink type="large">{result.value}</NuvoLink>
      </Anchor>
    ),
  };
};

const handleExternalLinkType = (
  result: SimpleFieldItemProps,
  emptyValue: string,
): SimpleFieldItemProps => {
  if (!result.value || result.value === emptyValue) return result;

  const href = result.value as string;
  return {
    ...result,
    render: () => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="record-preview-link">
        {href}
      </a>
    ),
  };
};

const handleListType = <T,>(
  result: SimpleFieldItemProps,
  field: Field<T>,
  record: T,
  emptyValue: string,
): SimpleFieldItemProps => {
  let listValue: { [key in keyof T]: string }[] = [];
  if (typeof record[field.dataField as keyof T] === 'string') {
    try {
      listValue = JSON.parse(record[field.dataField as keyof T] as string);
    } catch {
      listValue = [];
    }
  }
  if (Array.isArray(listValue) && listValue.length > 0) {
    return {
      ...result,
      render: () => (
        <ul className="list-field-ul">
          {listValue.map((item, index) => (
            <li key={index}>{item[field.valueExpr as keyof T]}</li>
          ))}
        </ul>
      ),
    };
  }
  return { ...result, value: emptyValue };
};

const handleOnClick = (
  result: SimpleFieldItemProps,
  onClick?: () => void,
): SimpleFieldItemProps => {
  if (result.render || !onClick) return result;

  return {
    ...result,
    render: () => (
      <NuvoLink type="large" className="record-preview-link" onClick={onClick}>
        {result.value}
      </NuvoLink>
    ),
  };
};

const mapField = <T,>(
  field: Field<T>,
  record: T,
  navigate?: (url: string) => void,
  emptyValue = 'Unknown',
): SimpleFieldItemProps => {
  const { label, render, onClick, dataType, cssClass, url, ...rest } = field;
  let result: SimpleFieldItemProps = {
    ...rest,
    itemType: 'simple',
    cssClass: cssClass,
    render,
    label,
    visible: true,
    value: getFieldValue(field, record, emptyValue),
  };

  if (dataType === 'date') {
    result = handleDateType(result, field, record, emptyValue);
  } else if (dataType === 'datetime') {
    result = handleDateTimeType(result, field, record, emptyValue);
  } else if (dataType === 'link') {
    result = handleLinkType(result, field, record, navigate);
  } else if (dataType === 'external-link') {
    result = handleExternalLinkType(result, emptyValue);
  } else if (dataType === 'list') {
    result = handleListType(result, field, record, emptyValue);
  }

  result = handleOnClick(result, onClick);

  return result;
};

const mapGroup = <T,>(
  group: Group<T>,
  record: T,
  navigate?: (url: string) => void,
  emptyValue = 'Unknown',
): FieldItemProps<T> => {
  const { items, ...rest } = group;

  const result: FieldItemProps<T> = {
    ...rest,
    items: mapFieldItems(items ?? [], record, navigate, emptyValue),
    itemType: 'group',
  };

  return result;
};

const isGroup = <T,>(item: RecordItem<T>): item is Group<T> => {
  return 'items' in item;
};

const mapFieldItem = <T,>(
  item: RecordItem<T>,
  record: T,
  navigate?: (url: string) => void,
  emptyValue = 'Unknown',
): FieldItemProps<T> =>
  isGroup(item)
    ? mapGroup(item, record, navigate, emptyValue)
    : mapField(item, record, navigate, emptyValue);

const mapFieldItems = <T,>(
  items: RecordItem<T>[],
  record: T,
  navigate?: (url: string) => void,
  emptyValue = 'Unknown',
): FieldItemProps<T>[] => items.map((item) => mapFieldItem(item, record, navigate, emptyValue));

export default mapFieldItems;
